import { Clock, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useNavigate, useParams } from "react-router-dom"
import { motion } from "framer-motion"
import { ObjectiveCard } from "@/modules/planner/components/comunications/objective-card"
import useGetWeeklyObjectivesApproval from "@/modules/planner/queries/use-get-weekly-objectives-approval"
import { formatDuration } from "@/shared/utils/date-fn"
import { intervalToDuration } from "date-fns"
import { useMemo } from "react"

export default function SavedActivities() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { data, isPending, isError } = useGetWeeklyObjectivesApproval(id as string)

  const totalTime = useMemo(() => {
    if (!data) return
    const duration = intervalToDuration({ start: 0, end: data?.skippedActivitiesTime * 1000 })

    return formatDuration(duration).min({
      hours: true,
      minutes: true,
    })
  }, [data])

  if (isError) {
    return (
      <section className="z-5 absolute left-0 top-0 flex size-full flex-col items-center justify-center gap-4 bg-white px-2 py-2 lg:gap-6 lg:p-10">
        <h1 className="text-center text-3xl font-bold text-primary">
          Error al obtener el objetivo semanal
        </h1>
        <p className="text-center font-semibold text-muted-foreground">
          Por favor, intenta nuevamente más tarde.
        </p>
        <Button onClick={() => navigate("/my-situation", { replace: true })}>
          Volver al inicio
        </Button>
      </section>
    )
  }

  return (
    <section className="z-5 absolute left-0 top-0 flex size-full flex-col items-center justify-center gap-4 bg-white px-2 py-2 lg:gap-6 lg:p-10">
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-6 top-6"
        onClick={() => navigate("/planner", { replace: true })}
      >
        <X className="!size-7 text-muted-foreground" />
      </Button>
      {/* Trophy icon with gradient background */}
      <div className="relative mb-4 flex items-center justify-center">
        <motion.div
          className="absolute size-28 rounded-full bg-gradient-to-b from-primary-50 to-primary-100"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        />
        <motion.div
          className="relative flex size-24 items-center justify-center rounded-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <p className="text-4xl font-bold text-primary-500">{data?.skippedActivities}</p>
        </motion.div>
      </div>

      {/* Title and subtitle */}
      <motion.h1
        className="text-center text-3xl font-bold text-primary"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.4 }}
      >
        Actividades ahorradas
      </motion.h1>
      <motion.p
        className="w-[70ch] text-center font-semibold text-muted-foreground"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.4 }}
      >
        ¡Bien ahí! 🙌 Algunas actividades se han marcado como completadas porque cumpliste tu
        objetivo ¡Te lo ganaste!
      </motion.p>

      {/* Stats cards */}
      <motion.div
        className="flex w-full max-w-fit items-center justify-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
      >
        {/* Saved activities */}
        <ObjectiveCard
          isPending={isPending}
          title="Tiempo de estudio ahorrado"
          Icon={Clock}
          value={totalTime ?? ""}
        />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
      >
        <Button
          className="mt-6 w-fit bg-primary-500 font-semibold"
          onClick={() => navigate("/planner", { replace: true })}
        >
          Continuar estudiando
        </Button>
      </motion.div>
    </section>
  )
}
